import { ApolloClient, gql } from '@apollo/client'
import kebabCase from 'lodash/kebabCase'
import { normalizeProjectSeasons } from '@/services/ProjectsService/normalizeSeasons'
import { GetAllEpisodeGuidsQuery, GetEpisodeByGuidQuery } from '@/types/codegen-federation'
import { GET_EPISODE_BY_GUID_QUERY } from '../queries'
import { Episode, Season } from '../types'

export async function findEpisodeGuidBySlug(
  client: ApolloClient,
  slug: string,
  projectSlug: string,
): Promise<string | undefined> {
  const { episodes } = await buildEpisodeGuidMap(client, projectSlug)
  return (
    episodes[slug] ||
    Object.entries(episodes).find(([key]) => {
      return slug.indexOf(key) > -1
    })?.[1]
  )
}

export async function findSlugByEpisodeGuid(client: ApolloClient, guid: string): Promise<string | undefined> {
  const { data } = await client.query<GetEpisodeByGuidQuery>({
    query: GET_EPISODE_BY_GUID_QUERY,
    variables: { guid },
    errorPolicy: 'all',
  })
  return data?.episode?.projectSlug ?? undefined
}

async function buildEpisodeGuidMap(
  client: ApolloClient,
  projectSlug: string,
): Promise<{ episodes: Record<string, string> }> {
  const { data } = await client.query<GetAllEpisodeGuidsQuery>({
    query: getAllEpisodes,
    variables: { slug: projectSlug },
    errorPolicy: 'all',
  })
  const episodes = {} as Record<string, string>

  // Normalize project to transform title.seasons into project.seasons
  const normalizedProject = normalizeProjectSeasons(data?.project ?? undefined)
  normalizedProject?.seasons?.forEach((season: Season) => {
    season.episodes?.forEach((episode: Episode) => {
      const seasonName = season.name
      const { name: episodeName, subtitle, guid } = episode

      episodes[getEpisodePathSlug({ seasonName, episodeName })] = guid
      episodes[getFriendlyEpisodePathSlug({ seasonName, episodeName, subtitle })] = guid
    })
  })

  return { episodes }
}

export const getAllEpisodes = gql`
  query getAllEpisodeGuids($slug: String!) {
    project(slug: $slug) {
      title {
        ... on ContentSeries {
          id
          seasons(first: 10) {
            edges {
              node {
                id
                name
                seasonNumber
                episodes(first: 100) {
                  edges {
                    node {
                      id
                      name
                      subtitle
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

interface GetFriendlyEpisodePathSlug {
  seasonName: string
  episodeName: string
  subtitle: string
}

export function getFriendlyEpisodePathSlug({ seasonName, episodeName, subtitle }: GetFriendlyEpisodePathSlug): string {
  return kebabCase(`${getEpisodePathSlug({ seasonName, episodeName })} ${subtitle}`)
}

interface GetEpisodePathSlug {
  seasonName: string
  episodeName: string
}

export function getEpisodePathSlug({ seasonName, episodeName }: GetEpisodePathSlug): string {
  return kebabCase(`${seasonName} ${episodeName}`)
}

export interface EpisodeIdentifiers {
  name: string
  guid: string
  subtitle: string
}
